import React, {Component, ReactNode} from 'react';
import {inject, observer} from 'mobx-react';
import SellNowSteamSkin from 'app/components/common/SellNowSteamSkin';
import {hideLastPopup, showPopup} from 'app/utils/modals/ModalsService';
import {scrollToTop} from 'app/utils';
import PrizeCard from 'app/components/reusable/PrizeCard';
import I18n from 'app/i18n';
import {lootboxToChat} from 'app/utils/share_to_chat';
import PrizeBackground from './popup/prize_background';
import BuyButton, {type BuyButtonProps} from 'app/components/shop/lootboxes/buy_button';
import SellArtifactPopup from './sell_artifact_popup';
import ActionButton from './action_button';
import {currencyItem} from 'app/utils/ShopItemPriceFormat';
import {prizeName} from 'app/components/my_profile/artifacts/popup/PrizeName';
import {userStore} from 'app/store/global';
import {openPopupClickable} from 'app/components/my_profile/hero/clickable';
import SmartLoader from 'app/components/reusable/SmartLoader';
import SpriteIcon from 'app/utils/sprites/SpriteIcon';
import {ArtifactStore} from 'app/store/ArtifactStore';
import {ShopLootboxStore} from 'app/store/ShopLootboxStore';
import InventoryCommonPropertiesInterface from 'app/interfaces/InventoryCommonPropertiesInterface';
import InventoryItemInterface from 'app/interfaces/InventoryItemInterface';
import LootInterface from 'app/interfaces/LootInterface';
import ShopLootboxInterface from 'app/interfaces/shop/ShopLootboxInterface';

const DELAY_OPENING = 500;

interface Props {
    artifact: InventoryItemInterface;
    commonProperties: InventoryCommonPropertiesInterface;
    shopLootboxStore?: ShopLootboxStore;
    artifactStore?: ArtifactStore;
}

interface State {
    currency: string;
    loadingButton: boolean;
}

class OpenedLootboxPopupContent extends Component<Props, State> {
    state: State = {
        currency: '',
        loadingButton: true
    };

    loadingButtonTimeout: ReturnType<typeof setTimeout> | null = null;

    componentDidMount(): void {
        this.updateCurrencyByLocation();
        this.updateLootbox();
        this.loadingButtonTimeout = setTimeout(() => this.setState({loadingButton: false}), DELAY_OPENING);

        if (this.isInventoryPage()) {
            this.props.artifactStore?.getUserInventory();
        }
    }

    componentWillUnmount(): void {
        if (this.loadingButtonTimeout) {
            clearTimeout(this.loadingButtonTimeout);
        }
    }

    updateLootbox = (): void => {
        const {shopLootboxStore, artifact: {slug}} = this.props;
        const {currency} = this.state;

        shopLootboxStore?.updateLootboxBySlug(slug, currency);
    };

    openMore = (): void => {
        const {shopLootboxStore, artifactStore, artifact} = this.props;

        this.updateLootbox();
        shopLootboxStore?.updateOpenNowStatus(true);

        if (this.isInventoryPage()) {
            const newArtifact = artifactStore?.inventory_items.find(
                (item: InventoryItemInterface) => item.recipe?.id.toString() === artifact.recipe?.id.toString()
            );

            openPopupClickable(newArtifact);
            return;
        }

        hideLastPopup('inventory-popup');
        scrollToTop();
    };

    buyMore = (): void => {
        const {shopLootboxStore, artifactStore, artifact} = this.props;

        this.updateLootbox();
        shopLootboxStore?.updateOpenNowStatus(true);

        if (this.isInventoryPage()) {
            artifactStore?.getUserInventory();
            setTimeout(() => {
                const newArtifact = artifactStore?.inventory_items.find(
                    (item: InventoryItemInterface) => item.recipe?.id.toString() === artifact.recipe?.id.toString()
                );

                openPopupClickable(newArtifact);
            }, DELAY_OPENING);
            return;
        }

        hideLastPopup('inventory-popup');
        scrollToTop();
    };

    shareToChat = (): void => {
        const {artifact: {id}} = this.props;

        lootboxToChat(id);
        hideLastPopup();
    };

    get prize(): LootInterface | undefined {
        const {commonProperties: {extra_data = []}} = this.props;
        const [prize] = extra_data;

        return prize;
    }

    get winArtifact(): InventoryItemInterface | undefined {
        const {commonProperties: {win_artifact}} = this.props;

        return win_artifact;
    }

    updateCurrencyByLocation = (): void => {
        const urlParams = new URLSearchParams(window.location.search);

        this.setState({currency: currencyItem(urlParams.get('currency') || '')});
    };

    sellArtifact = (): void => {
        const {winArtifact} = this;

        if (!winArtifact) {
            return;
        }

        showPopup(
            'sell-inventory',
            ({popupId}: {popupId: string}) => <SellArtifactPopup artifact={winArtifact} popupId={popupId}/>
        );
    };

    get showButtonSell(): boolean {
        const {restrict_artifact} = userStore.user;
        const {winArtifact} = this;
        const sellablePrize = Boolean(this.prize?.steam_skin || this.prize?.recipe);

        return Boolean(sellablePrize && winArtifact?.salable && !restrict_artifact);
    }

    shopLootbox = (shopLootboxStore?: ShopLootboxStore): ShopLootboxInterface | null => {
        const lootbox = shopLootboxStore?.lootbox;

        if (!lootbox || typeof lootbox !== 'object' || !('id' in lootbox)) {
            return null;
        }

        return lootbox as ShopLootboxInterface;
    };

    renderBuyOrOpenButton = (
        shopLootboxStore: ShopLootboxStore | undefined,
        totalInventoryItemsCount: number,
        currency: string
    ): ReactNode => {
        const lootbox = this.shopLootbox(shopLootboxStore);
        const userLootboxCount = lootbox?.user_lootbox_count || 0;
        const totalInventoryItems = totalInventoryItemsCount - Number(this.isInventoryPage());

        if (this.state.loadingButton) {
            return <SmartLoader/>;
        }

        if (userLootboxCount > 0 || totalInventoryItems > 0) {
            return <button
                className="button-adaptive button-adaptive_uppercase"
                onClick={this.openMore}
            >
                {I18n.t('open_more')}
            </button>;
        }

        if (lootbox?.id) {
            return <BuyButton
                {...lootbox as BuyButtonProps}
                currency={currency}
                customText={I18n.t('buy_more')}
                show_loading={false}
                afterBuy={this.buyMore}
            />;
        }

        return null;
    };

    isInventoryPage = (): boolean => window.location.pathname.includes('hero');

    render(): ReactNode {
        const {artifact: {total_inventory_items}, commonProperties, shopLootboxStore} = this.props;
        const {steam_skin, steam_trade} = commonProperties;
        const {currency} = this.state;
        const {prize, winArtifact} = this;

        return <div className="prize-won">
            <div className="prize-won-title">
                <h2>{I18n.t('congratulations_you_win')}</h2>
            </div>
            <div className="prize-won-name">
                <span className="prize-won-name__icon">
                    <SpriteIcon name="#svg-prize-icon"/>
                </span>
                <span className="prize-won-name__name">
                    {prize && prizeName(prize)}
                </span>
            </div>
            <div className="prize-won-image-wrapper">
                <div className="prize-won-image">
                    <div className="prize-won-adaptive-image">
                        <PrizeCard
                            loot={prize}
                            hideMeta
                            hideName
                        />
                    </div>
                    <PrizeBackground steam_skin={prize?.steam_skin}/>
                </div>
            </div>
            <div className="prize-won-buttons">
                <div className="row">
                    {this.renderBuyOrOpenButton(shopLootboxStore, total_inventory_items, currency)}
                </div>
                <div className="row row_group-button">
                    {steam_skin && <SellNowSteamSkin
                        popupId="inventory-popup"
                        steam_skin={steam_skin}
                        steam_trade={steam_trade}
                        btnRemoveClassBtn
                        btnClass="button-adaptive button-adaptive_full-thin"
                    />}
                    {this.showButtonSell && winArtifact && <ActionButton
                        {...winArtifact}
                        onClickHandler={this.sellArtifact}
                        btnText={I18n.t('sell')}
                        btnRemoveClassBtn
                        btnClass="button-adaptive button-adaptive_full-thin"
                    />}
                    <button className="button-adaptive button-adaptive_full-thin-icon" onClick={this.shareToChat}>
                        <span className="button-icon">
                            <SpriteIcon name="#svg-arrow-link"/>
                        </span>
                        <span className="button-name">{I18n.t('in_chat')}</span>
                    </button>
                </div>
            </div>
        </div>;
    }
}

export default inject('shopLootboxStore', 'artifactStore')(observer(OpenedLootboxPopupContent));
